import React, { useState, useEffect, useCallback } from 'react';
import {
  Users, UserCheck, UserX, UserMinus,
  Search, Filter, Download, AlertCircle,
} from 'lucide-react';
import * as dpService from 'features/dp/services/dpService';
import { useCurrentCompany } from 'hooks/useCurrentCompany';
import FuncionarioDetalhe from './FuncionarioDetalhe';
import FuncionarioForm from './FuncionarioForm';
import ImportarIXCModal from './ImportarIXCModal';
import SelecionarUnidadeModal from './SelecionarUnidadeModal';

/* ── Status badges ─────────────────────────────────────────── */
const STATUS_MAP = {
  ativo:    { label: 'Ativo',    className: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' },
  ferias:   { label: 'Férias',   className: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
  afastado: { label: 'Afastado', className: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' },
  demitido: { label: 'Demitido', className: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' },
};

/* ── KPI card ──────────────────────────────────────────────── */
function KpiCard({ icon, label, valor, cor }) {
  return (
    <div className="bg-[color:var(--surface)] border border-[color:var(--border)] rounded-2xl p-4 flex items-center gap-3">
      <div className={`${cor} text-white p-2 rounded-xl`}>{icon}</div>
      <div>
        <div className="text-xl font-bold text-[color:var(--text)]">{valor ?? '—'}</div>
        <div className="text-xs text-[color:var(--text-muted)]">{label}</div>
      </div>
    </div>
  );
}

/* ── Custom hooks (extracted from bundle) ──────────────────── */
function useFuncionarios(initialFilters = {}) {
  const [funcionarios, setFuncionarios] = useState(null);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState(null);
  const [filtros, setFiltros] = useState(initialFilters);

  const carregar = useCallback(async (extraParams = {}) => {
    setLoading(true);
    setErro(null);
    try {
      const res = await dpService.listarFuncionarios({ ...filtros, ...extraParams });
      setFuncionarios(res.data.data);
    } catch (err) {
      setErro(
        err.response?.data?.message || 'Erro ao carregar funcionários'
      );
    } finally {
      setLoading(false);
    }
  }, [filtros]);

  useEffect(() => { carregar(); }, [carregar]);

  return { funcionarios, loading, erro, filtros, setFiltros, carregar };
}

function useDashboard() {
  const [dados, setDados] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    dpService.dashboardDP()
      .then((res) => setDados(res.data.data))
      .catch(() => setDados(null))
      .finally(() => setLoading(false));
  }, []);

  return { dados, loading };
}

function useFiltrosOpcoes() {
  const [departamentos, setDepartamentos] = useState([]);
  const [cargos, setCargos] = useState([]);

  useEffect(() => {
    Promise.all([
      dpService.listarDepartamentos({ is_active: 1 }),
      dpService.listarCargos({ is_active: 1 }),
    ]).then(([resD, resC]) => {
      setDepartamentos(resD.data.data ?? []);
      setCargos(resC.data.data ?? []);
    }).catch(() => {});
  }, []);

  return { departamentos, cargos };
}

/* ── Status filter chips ───────────────────────────────────── */
const STATUS_FILTERS = [
  { value: '',         label: 'Todos' },
  { value: 'ativo',    label: 'Ativos' },
  { value: 'ferias',   label: 'Férias' },
  { value: 'afastado', label: 'Afastados' },
  { value: 'demitido', label: 'Demitidos' },
];

/* ── Main component ────────────────────────────────────────── */
export default function FuncionariosTab({ podeAdicionar = false }) {
  const {
    funcionarios: rawFuncionarios,
    loading,
    erro,
    filtros,
    setFiltros,
    carregar,
  } = useFuncionarios();

  const { dados: dashboard } = useDashboard();
  const { departamentos, cargos } = useFiltrosOpcoes();
  const { currentCompany } = useCurrentCompany();

  const [mostrarForm, setMostrarForm] = useState(false);
  const [mostrarImportarIXC, setMostrarImportarIXC] = useState(false);
  const [mostrarSelecionarUnidade, setMostrarSelecionarUnidade] = useState(false);
  const [unidadeSelecionada, setUnidadeSelecionada] = useState(null);
  const [funcionarioEditar, setFuncionarioEditar] = useState(null);
  const [detalheId, setDetalheId] = useState(null);
  const [busca, setBusca] = useState('');

  const isMovFit = dpService.isMovFit(currentCompany?.name ?? '');

  const handleAbrirImportar = () => {
    if (isMovFit) {
      setMostrarSelecionarUnidade(true);
    } else {
      setMostrarImportarIXC(true);
    }
  };

  // normalise: API may return { data: [...] } or directly [...]
  const funcionarios = rawFuncionarios?.data ?? rawFuncionarios ?? [];

  /* ── Detail view ──────────────────────────────────────────── */
  if (detalheId) {
    return (
      <FuncionarioDetalhe
        funcionarioId={detalheId}
        onVoltar={() => setDetalheId(null)}
        podeEditar={podeAdicionar}
      />
    );
  }

  /* ── List view ────────────────────────────────────────────── */
  return (
    <div className="space-y-6">
      {/* KPIs */}
      {dashboard && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <KpiCard icon={<Users size={18} />}      label="Total"     valor={dashboard.total}     cor="bg-teal-600" />
          <KpiCard icon={<UserCheck size={18} />}   label="Ativos"    valor={dashboard.ativos}    cor="bg-green-600" />
          <KpiCard icon={<UserMinus size={18} />}   label="Afastados" valor={dashboard.afastados} cor="bg-yellow-500" />
          <KpiCard icon={<UserX size={18} />}       label="Demitidos" valor={dashboard.demitidos} cor="bg-red-500" />
        </div>
      )}

      {/* Search + new employee button */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            setFiltros((prev) => ({ ...prev, busca }));
          }}
          className="flex gap-2 w-full sm:w-auto"
        >
          <div className="relative flex-1 sm:w-72">
            <Search
              size={15}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--text-muted)]"
            />
            <input
              type="text"
              placeholder="Buscar por nome, CPF ou matrícula..."
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              className="w-full pl-9 pr-3 py-2 text-sm rounded-xl border border-[color:var(--border)] bg-[color:var(--surface)] text-[color:var(--text)] focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
          </div>
          <button
            type="submit"
            className="px-3 py-2 text-sm rounded-xl border border-[color:var(--border)] bg-[color:var(--surface)] hover:bg-[color:var(--surface-muted)] transition-colors"
          >
            <Filter size={15} />
          </button>
        </form>

        {podeAdicionar && (
          <button
            onClick={handleAbrirImportar}
            className="flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-xl bg-teal-600 text-white hover:bg-teal-700 transition-colors"
          >
            <Download size={15} />
            Importar
          </button>
        )}
      </div>

      {/* Filtros: status chips + departamento + cargo */}
      <div className="flex flex-wrap items-center gap-2">
        {STATUS_FILTERS.map((sf) => (
          <button
            key={sf.value}
            onClick={() => setFiltros((prev) => ({ ...prev, status: sf.value }))}
            className={`px-3 py-1 text-xs rounded-full font-medium transition-colors ${
              filtros.status === sf.value || (sf.value === '' && !filtros.status)
                ? 'bg-teal-600 text-white'
                : 'bg-[color:var(--surface-muted)] text-[color:var(--text-muted)] hover:bg-[color:var(--surface)]'
            }`}
          >
            {sf.label}
          </button>
        ))}

        {departamentos.length > 0 && (
          <select
            value={filtros.departamento_id ?? ''}
            onChange={(e) => setFiltros((prev) => ({ ...prev, departamento_id: e.target.value || undefined }))}
            className="px-3 py-1 text-xs rounded-full font-medium border border-[color:var(--border)] bg-[color:var(--surface-muted)] text-[color:var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-teal-500 cursor-pointer"
          >
            <option value="">Todos os departamentos</option>
            {departamentos.map((d) => (
              <option key={d.id} value={d.id}>{d.nome}</option>
            ))}
          </select>
        )}

        {cargos.length > 0 && (
          <select
            value={filtros.cargo_id ?? ''}
            onChange={(e) => setFiltros((prev) => ({ ...prev, cargo_id: e.target.value || undefined }))}
            className="px-3 py-1 text-xs rounded-full font-medium border border-[color:var(--border)] bg-[color:var(--surface-muted)] text-[color:var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-teal-500 cursor-pointer"
          >
            <option value="">Todos os cargos</option>
            {cargos.map((c) => (
              <option key={c.id} value={c.id}>{c.nome}</option>
            ))}
          </select>
        )}

        {isMovFit && (
          <select
            value={filtros.unidade ?? ''}
            onChange={(e) => setFiltros((prev) => ({ ...prev, unidade: e.target.value || undefined }))}
            className="px-3 py-1 text-xs rounded-full font-medium border border-[color:var(--border)] bg-[color:var(--surface-muted)] text-[color:var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-teal-500 cursor-pointer"
          >
            <option value="">Todas as unidades</option>
            {dpService.MOV_FIT_UNIDADES.map((u) => (
              <option key={u.id} value={u.label}>{u.label}</option>
            ))}
          </select>
        )}

        {(filtros.departamento_id || filtros.cargo_id || filtros.unidade) && (
          <button
            onClick={() => setFiltros((prev) => ({ ...prev, departamento_id: undefined, cargo_id: undefined, unidade: undefined }))}
            className="px-3 py-1 text-xs rounded-full font-medium text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
          >
            Limpar filtros
          </button>
        )}
      </div>

      {/* Loading / Error / Empty / Table */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin w-8 h-8 border-2 border-teal-600 border-t-transparent rounded-full" />
        </div>
      ) : erro ? (
        <div className="flex items-center gap-2 text-red-500 bg-red-50 dark:bg-red-900/20 p-4 rounded-xl">
          <AlertCircle size={16} />
          <span className="text-sm">{erro}</span>
        </div>
      ) : funcionarios.length === 0 ? (
        <div className="text-center py-12 text-[color:var(--text-muted)]">
          <Users size={40} className="mx-auto mb-3 opacity-40" />
          <p className="font-medium">Nenhum funcionário encontrado</p>
          <p className="text-sm mt-1">
            {podeAdicionar
              ? 'Clique em "Importar" para trazer funcionários do IXC/Pacto.'
              : 'Ajuste os filtros ou verifique suas permissões.'}
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[color:var(--border)]">
                <th className="text-left py-3 px-3 font-semibold text-[color:var(--text-muted)] text-xs uppercase tracking-wider">Matrícula</th>
                <th className="text-left py-3 px-3 font-semibold text-[color:var(--text-muted)] text-xs uppercase tracking-wider">Nome</th>
                <th className="text-left py-3 px-3 font-semibold text-[color:var(--text-muted)] text-xs uppercase tracking-wider hidden sm:table-cell">Cargo</th>
                <th className="text-left py-3 px-3 font-semibold text-[color:var(--text-muted)] text-xs uppercase tracking-wider hidden md:table-cell">Departamento</th>
                <th className="text-left py-3 px-3 font-semibold text-[color:var(--text-muted)] text-xs uppercase tracking-wider hidden lg:table-cell">Admissão</th>
                <th className="text-left py-3 px-3 font-semibold text-[color:var(--text-muted)] text-xs uppercase tracking-wider">Status</th>
                <th className="text-left py-3 px-3 font-semibold text-[color:var(--text-muted)] text-xs uppercase tracking-wider">Ações</th>
              </tr>
            </thead>
            <tbody>
              {funcionarios.map((func) => {
                const status = STATUS_MAP[func.status] || STATUS_MAP.ativo;
                return (
                  <tr
                    key={func.id}
                    className="border-b border-[color:var(--border)] hover:bg-[color:var(--surface-muted)] transition-colors"
                  >
                    <td className="py-3 px-3 font-mono text-xs text-[color:var(--text-muted)]">
                      {func.matricula}
                    </td>
                    <td className="py-3 px-3">
                      <div
                        className="font-medium text-[color:var(--text)] cursor-pointer hover:text-teal-600 hover:underline"
                        onClick={() => setDetalheId(func.id)}
                      >
                        {func.pessoa?.nome}
                      </div>
                      <div className="text-xs text-[color:var(--text-muted)]">
                        {func.pessoa?.cpf}
                      </div>
                    </td>
                    <td className="py-3 px-3 text-[color:var(--text-muted)] hidden sm:table-cell">
                      {func.cargo?.nome ?? '—'}
                    </td>
                    <td className="py-3 px-3 text-[color:var(--text-muted)] hidden md:table-cell">
                      {func.departamento?.nome ?? '—'}
                    </td>
                    <td className="py-3 px-3 text-[color:var(--text-muted)] hidden lg:table-cell">
                      {func.dt_admissao
                        ? new Date(func.dt_admissao).toLocaleDateString('pt-BR')
                        : '—'}
                    </td>
                    <td className="py-3 px-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${status.className}`}>
                        {status.label}
                      </span>
                    </td>
                    <td className="py-3 px-3">
                      {podeAdicionar && (
                        <button
                          onClick={() => { setFuncionarioEditar(func); setMostrarForm(true); }}
                          className="text-xs text-teal-600 hover:underline"
                        >
                          Editar
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal selecionar unidade (MOV Fit) */}
      {mostrarSelecionarUnidade && (
        <SelecionarUnidadeModal
          onSelecionar={(unidade) => {
            setUnidadeSelecionada(unidade);
            setMostrarSelecionarUnidade(false);
            setMostrarImportarIXC(true);
          }}
          onCancelar={() => setMostrarSelecionarUnidade(false)}
        />
      )}

      {/* Modal importar IXC */}
      {mostrarImportarIXC && (
        <ImportarIXCModal
          companyName={currentCompany?.name ?? 'MOV Fibra'}
          unidadeId={unidadeSelecionada?.id ?? null}
          onSelecionar={(dadosIXC) => {
            setMostrarImportarIXC(false);
            setUnidadeSelecionada(null);
            setFuncionarioEditar(dadosIXC);
            setMostrarForm(true);
          }}
          onCancelar={() => { setMostrarImportarIXC(false); setUnidadeSelecionada(null); }}
        />
      )}

      {/* Form modal */}
      {mostrarForm && (
        <FuncionarioForm
          funcionario={funcionarioEditar}
          onSalvo={() => { setMostrarForm(false); setFuncionarioEditar(null); carregar(); }}
          onCancelar={() => { setMostrarForm(false); setFuncionarioEditar(null); }}
        />
      )}
    </div>
  );
}
